import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Avatar,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import { deepPurple } from "@mui/material/colors";
import { getUsername, getRole } from "../Utility/helper";

const UserProfile = () => {
  const username = getUsername();
  const role = getRole();
  const [profile, setProfile] = useState(null);
  const [originalProfile, setOriginalProfile] = useState({});
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState({ type: "", message: "" });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(
          `https://core-bankers-backend.vercel.app/profile/${username}`
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Failed to load profile");
        setProfile(data);
        setOriginalProfile(data);
      } catch (err) {
        setStatus({ type: "error", message: err.message });
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [username]);

  const handleChange = (e) => {
    setProfile((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleUpdate = async () => {
    setStatus({ type: "", message: "" });

    if ((password || confirmPassword) && password !== confirmPassword) {
      setStatus({ type: "error", message: "Passwords do not match" });
      return;
    }

    const updates = {};
    for (const key in profile) {
      if (profile[key] !== originalProfile[key]) {
        updates[key] = profile[key];
      }
    }

    if (password) {
      updates.password = password;
    }

    if (Object.keys(updates).length === 0) {
      setStatus({ type: "info", message: "No changes to update" });
      return;
    }

    try {
      const res = await fetch(
        `https://core-bankers-backend.vercel.app/profile/${username}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(updates),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Update failed");
      setStatus({ type: "success", message: "Profile updated successfully!" });
      setOriginalProfile({ ...profile });
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    }
  };

  if (loading) return <CircularProgress />;
  if (!profile) return <Alert severity="error">No profile found</Alert>;

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Profile Header */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 3,
        }}
        elevation={4}
      >
        <Avatar
          sx={{
            bgcolor: deepPurple[500],
            width: 72,
            height: 72,
            fontSize: 28,
          }}
        >
          {profile.first_name?.[0] || username[0]}
        </Avatar>
        <Box>
          <Typography variant="h6">
            {profile.first_name} {profile.last_name}
          </Typography>
          <Typography color="textSecondary">@{username}</Typography>
          <Typography color="primary">{role}</Typography>
          <Typography variant="body2" color="textSecondary">
            {profile.company || "No company info"}
          </Typography>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ p: 3 }} elevation={2}>
        {status.message && (
          <Alert severity={status.type} sx={{ mb: 2 }}>
            {status.message}
          </Alert>
        )}

        <Tabs
          value={tab}
          onChange={(e, newVal) => setTab(newVal)}
          sx={{ mb: 3 }}
        >
          <Tab label="Profile Info" />
          <Tab label="Banking Details" />
          <Tab label="Security" />
        </Tabs>

        {/* Profile Info */}
        {tab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                name="first_name"
                value={profile.first_name || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                name="last_name"
                value={profile.last_name || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Mobile"
                name="mobile"
                value={profile.mobile || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                name="email"
                value={profile.email || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Company"
                name="company"
                value={profile.company || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>
        )}

        {/* Banking Details */}
        {tab === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Bank Name"
                name="bank_name"
                value={profile.bank_name || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Account Number"
                name="account_number"
                value={profile.account_number || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="IFSC"
                name="ifsc"
                value={profile.ifsc || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="UPI ID"
                name="upi_id"
                value={profile.upi_id || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>
        )}

        {/* Security */}
        {tab === 2 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="New Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        )}

        {/* Sticky Update Button */}
        <Divider sx={{ my: 3 }} />
        <Box textAlign="center">
          <Button
            variant="contained"
            onClick={handleUpdate}
            sx={{
              px: 4,
              py: 1.2,
              fontWeight: "bold",
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            Update Profile
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default UserProfile;